/**
 * =========================================================================================
 * Billing - Payment Method Management
 * =========================================================================================
 */

import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as Icon from 'ubs-icons';
import { COLOR } from 'ubs-framework/v2/common/constant';
import UCustomScrollBar from 'ubs-framework/v2/UCustomScrollBar';
import UButton from 'ubs-framework/v2/UButton';
import UTags from 'ubs-framework/v2/UTags';
import useUAlert from 'ubs-framework/v2/UAlert';
import UIcon from 'ubs-framework/v2/UIcon';
import UPopup from 'ubs-framework/v2/UPopup';
import { getKeyName, numberWithComaToFixed, showToastError, showToastSuccess } from 'ubs-framework/v2/common/function';
import { dateFormatter } from 'ubs-framework/v2/common/function';
import { sendData } from '../../../../store/actions';
import useSocket from '../../../common/useSocket';
import { getCashfreeInstance } from '../common/cashfree-loader';
import Skeleton from 'ubs-framework/v2/skeleton';
import { useDispatch } from 'react-redux';
import AppHeader from '../../../common/components/setting-app-header';
import { PAYMENT_CARD_TYPE } from '../../../../common/constant';

const PaymentMethod = () => {
    const alert = useUAlert();
    const history = useHistory();
    const dispatch = useDispatch();
    const _ORG_ID = useSelector(e => e?.common?.orgId);

    // paymentMethodStatus from API: "AVAILABLE" | "PENDING_AUTHORIZATION" | "NO_SUBSCRIPTION" | "UNAVAILABLE"
    const [paymentMethodStatus, setPaymentMethodStatus] = useState(null);

    const [canUpdate, setCanUpdate] = useState(false);
    const [currentPaymentMethod, setCurrentPaymentMethod] = useState(null);
    const [savedInstruments, setSavedInstruments] = useState([]);
    const [subscription, setSubscription] = useState(null);
    const [dataLoading, setDataLoading] = useState(true);
    const [deleteLoadingId, setDeleteLoadingId] = useState(null);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [cancelModLoading, setCancelModLoading] = useState(false);
    const [pendingModError, setPendingModError] = useState(null);

    // Ref to capture the instrument ID being deleted (for 404 error removal from state)
    const deletingIdRef = useRef(null);

    const getData = () => {
        setDataLoading(true);
        sendData(dispatch, 'billing|cashfree|subscription|paymentMethod', {});
    };

    const { off, refresh } = useSocket(res => {
        if (!res.error) {
            switch (res.event) {
                case 'billing|cashfree|subscription|paymentMethod':
                    setDataLoading(false);
                    setPaymentMethodStatus(res?.data?.paymentMethodStatus || null);
                    setCanUpdate(res?.data?.canUpdatePaymentMethod || false);
                    setCurrentPaymentMethod(res?.data?.currentPaymentMethod || null);
                    setSavedInstruments(res?.data?.savedInstruments || []);
                    setSubscription(res?.data?.subscription || null);
                    break;

                case 'billing|cashfree|subscription|deletePaymentInstrument':
                    deletingIdRef.current = null;
                    setDeleteLoadingId(null);
                    if (res?.data?.success) {
                        const deletedId = res?.data?.instrumentId;
                        setSavedInstruments(prev => prev.filter(i => i.instrumentId !== deletedId));
                        showToastSuccess('Card removed successfully.');
                    }
                    break;

                case 'billing|cashfree|subscription|updatePaymentMethod':
                    setUpdateLoading(false);
                    setPendingModError(null);
                    handleCheckoutOpen(res?.data);
                    break;

                case 'billing|cashfree|subscription|cancelModification':
                    setCancelModLoading(false);
                    setPendingModError(null);
                    showToastSuccess(res?.data?.message || 'Modification cancelled. You can now update your payment method.');
                    setUpdateLoading(true);
                    sendData(dispatch, 'billing|cashfree|subscription|updatePaymentMethod', {});
                    break;

                default:
                    return true;
            }
        } else {
            if (res.event === 'billing|cashfree|subscription|deletePaymentInstrument') {
                const failedId = deletingIdRef.current;
                deletingIdRef.current = null;
                setDeleteLoadingId(null);
                if (res?.code === 404) {
                    // Card already deleted on server — remove from UI silently
                    if (failedId) {
                        setSavedInstruments(prev => prev.filter(i => i.instrumentId !== failedId));
                    }
                    showToastSuccess('Card was already removed.');
                } else if (res?.code === 502) {
                    showToastError('Payment gateway error. Please try again.');
                } else {
                    showToastError(res?.data?.error || 'Failed to remove card.');
                }
            } else if (res.event === 'billing|cashfree|subscription|updatePaymentMethod') {
                setUpdateLoading(false);
                if (res?.code === 409 && res?.data?.action === 'RESOLVE_PENDING_MODIFICATION') {
                    setPendingModError(res?.data);
                } else {
                    setPendingModError(null);
                    showToastError(res?.data?.error || res?.message || 'Failed to update payment method.');
                }
            } else if (res.event === 'billing|cashfree|subscription|cancelModification') {
                setCancelModLoading(false);
                showToastError(res?.data?.error || 'Failed to cancel modification. Please try again.');
            } else {
                setDataLoading(false);
                showToastError(res?.data?.error || 'Something went wrong. Please try again.');
            }
        }
    });

    useEffect(() => {
        refresh();
        getData();
        return () => off();
    }, []);

    // Open Cashfree subscriptions checkout with the session ID from updatePaymentMethod
    const handleCheckoutOpen = async (data) => {
        const sessionId = data?.subscription_session_id;
        if (!sessionId) {
            showToastError('Unable to start checkout. Please try again.');
            return;
        }
        try {
            const cashfreeInstance = await getCashfreeInstance();
            await cashfreeInstance.subscriptionsCheckout({
                subsSessionId: sessionId,
                redirectTarget: '_self',
            });
        } catch (err) {
            showToastError('Failed to open payment gateway. Please try again.');
        }
    };

    const handleDeleteCard = (instrument) => {
        alert.show({
            title: 'Remove Card',
            description: `Remove <strong>${instrument.displayLabel}</strong>? This card will no longer be saved.`,
            close: 'Cancel',
            submit: 'Remove',
            icon: <Icon.Delete fill={COLOR.u_red} />,
            onSubmit: () => {
                deletingIdRef.current = instrument.instrumentId;
                setDeleteLoadingId(instrument.instrumentId);
                sendData(dispatch, 'billing|cashfree|subscription|deletePaymentInstrument', {
                    instrumentId: instrument.instrumentId,
                });
            },
        });
    };

    const handleChangePaymentMethod = () => {
        if (updateLoading) return;
        setUpdateLoading(true);
        setPendingModError(null);
        sendData(dispatch, 'billing|cashfree|subscription|updatePaymentMethod', {});
    };

    // Cancel a pending/scheduled modification — only subscriptionId needed per API spec
    const handleCancelModification = () => {
        setCancelModLoading(true);
        sendData(dispatch, 'billing|cashfree|subscription|cancelModification', {
            subscriptionId: subscription?.subscriptionId,
        });
    };

    // For AWAITING_PAYMENT pending modifications: open Cashfree checkout with proratedOrder session
    const handleCompleteModPayment = async () => {
        const paymentLink = pendingModError?.proratedOrder?.paymentLink;
        if (!paymentLink) {
            history.push(`/${_ORG_ID}/manage/subscriptions/payments`);
            return;
        }
        try {
            const cashfreeInstance = await getCashfreeInstance();
            await cashfreeInstance.checkout({
                paymentSessionId: paymentLink,
                redirectTarget: '_self',
            });
        } catch (err) {
            showToastError('Failed to open payment gateway. Please try again.');
        }
    };

    const openPendingPayment = async () => {
        try {
            const cashfreeInstance = await getCashfreeInstance();
            await cashfreeInstance.subscriptionsCheckout({
                subsSessionId: subscription?.subscriptionSessionId,
                redirectTarget: '_self',
            });
        } catch (err) {
            showToastError('Failed to open payment gateway. Please try again.');
        }
    };



    const isCardExpired = subscription?.statusLabel === 'CARD_EXPIRED';
    const hasExpiryWarning = subscription?.cardExpiryWarning;

    const getPaymentMethodTypeLabel = (type) => {
        const labels = {
            CARD: 'Card',
            UPI: 'UPI Autopay',
            ENACH: 'eNACH Bank Mandate',
            PNACH: 'pNACH Bank Mandate',
            UNKNOWN: 'Payment Method',
        };
        return labels[type] || 'Payment Method';
    };

    const renderPaymentMethodDetails = (method) => {
        if (!method) return null;
        const { type, card, upi, bankAccount } = method;

        if (type === 'CARD' && card) {
            return (
                <div className="u_row">
                    {card.network && (
                        <div className="u_col-3">
                            <p className="u_small-font u_color-font u_mb-4">Network</p>
                            <p className="u_small-font u_fw-600 u_color-title">{card.network}</p>
                        </div>
                    )}
                    {card.cardType && (
                        <div className="u_col-3">
                            <p className="u_small-font u_color-font u_mb-4">Type</p>
                            <p className="u_small-font u_fw-600 u_color-title">
                                {getKeyName(PAYMENT_CARD_TYPE, card?.cardType)?.label || card?.cardType}
                            </p>
                        </div>
                    )}
                    {card.expiryMonth && card.expiryYear && (
                        <div className="u_col-3">
                            <p className="u_small-font u_color-font u_mb-4">Expiry</p>
                            <p className="u_small-font u_fw-600 u_color-title">{card.expiryMonth}/{card.expiryYear}</p>
                        </div>
                    )}
                    {card.bankName && (
                        <div className="u_col-3">
                            <p className="u_small-font u_color-font u_mb-4">Bank</p>
                            <p className="u_small-font u_fw-600 u_color-title">{card.bankName}</p>
                        </div>
                    )}
                </div>
            );
        }

        if (type === 'UPI' && upi) {
            return (
                <div className="u_row">
                    <div className="u_col-6">
                        <p className="u_small-font u_color-font u_mb-4">UPI ID</p>
                        <p className="u_small-font u_fw-600 u_color-title">{upi.upiId}</p>
                    </div>
                </div>
            );
        }

        if ((type === 'ENACH' || type === 'PNACH') && bankAccount) {
            return (
                <div className="u_row">
                    {bankAccount.maskedAccountNumber && (
                        <div className="u_col-3">
                            <p className="u_small-font u_color-font u_mb-4">Account</p>
                            <p className="u_small-font u_fw-600 u_color-title">{bankAccount.maskedAccountNumber}</p>
                        </div>
                    )}
                    {bankAccount.ifsc && (
                        <div className="u_col-3">
                            <p className="u_small-font u_color-font u_mb-4">IFSC</p>
                            <p className="u_small-font u_fw-600 u_color-title">{bankAccount.ifsc}</p>
                        </div>
                    )}
                    {bankAccount.holderName && (
                        <div className="u_col-3">
                            <p className="u_small-font u_color-font u_mb-4">Account Holder</p>
                            <p className="u_small-font u_fw-600 u_color-title">{bankAccount.holderName}</p>
                        </div>
                    )}
                    {bankAccount.bankCode && (
                        <div className="u_col-3">
                            <p className="u_small-font u_color-font u_mb-4">Bank</p>
                            <p className="u_small-font u_fw-600 u_color-title">{bankAccount.bankCode}</p>
                        </div>
                    )}
                    {bankAccount.accountType && (
                        <div className="u_col-3 u_mt-10">
                            <p className="u_small-font u_color-font u_mb-4">Account Type</p>
                            <p className="u_small-font u_fw-600 u_color-title">{bankAccount.accountType}</p>
                        </div>
                    )}
                </div>
            );
        }

        return null;
    };

    const renderPaymentMethodZone = () => {
        if (dataLoading) {
            return (
                <div className="u_px-20 u_py-16 u_bb-light">
                    <div className="u_row">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="u_col-3">
                                <Skeleton className="u_mb-10" height={15} />
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (paymentMethodStatus === 'NO_SUBSCRIPTION') {
            return (
                <div className="u_px-20 u_py-24 u_text-center u_bb-light">
                    <div className="u_mb-14 u_ib">
                        <UIcon color={COLOR?.u_icon_bg} thumb={46} icon={<Icon.CreditCard size={24} fill={COLOR?.u_icon_bg} />} type="square" />
                    </div>
                    <p className="u_font-16 u_fw-600 u_color-title u_mb-6">No Subscription Found</p>
                    <p className="u_small-font u_color-font u_mb-16">Create a subscription to set up your payment method.</p>
                    <div className="u_ib">
                        <UButton cn="u_payment-btn" type="Create Subscription" onClick={() => history.push(`/${_ORG_ID}/manage/pricing-plan`)} />
                    </div>
                </div>
            );
        }

        if (paymentMethodStatus === 'PENDING_AUTHORIZATION') {
            return (
                <div className="u_px-20 u_py-24 u_text-center u_bb-light">
                    <div className="u_mb-14 u_ib">
                        <UIcon color={COLOR?.u_orange} thumb={46} icon={<Icon.Clock size={24} fill={COLOR?.u_orange} />} type="square" />
                    </div>
                    <p className="u_font-16 u_fw-600 u_color-title u_mb-6">Payment Method Not Set</p>
                    <p className="u_small-font u_color-font u_mb-16">Subscription checkout is pending. Complete checkout to activate your payment method.</p>
                    <div className="u_ib">
                        <UButton cn="u_payment-btn" type="Complete Checkout" onClick={() => openPendingPayment()} />
                    </div>
                </div>
            );
        }

        if (paymentMethodStatus === 'UNAVAILABLE') {
            return (
                <div className="u_px-20 u_py-24 u_text-center u_bb-light">
                    <div className="u_mb-14 u_ib">
                        <UIcon color={COLOR?.u_icon_bg} thumb={46} icon={<Icon.CreditCard size={24} fill={COLOR?.u_icon_bg} />} type="square" />
                    </div>
                    <p className="u_font-16 u_fw-600 u_color-title u_mb-6">No Active Payment Method</p>
                    <p className="u_small-font u_color-font u_mb-16">Subscription is inactive. Create a new subscription to set up payment.</p>
                    <div className="u_ib">
                        <UButton cn="u_payment-btn" type="Create New Subscription" onClick={() => history.push(`/${_ORG_ID}/manage/pricing-plan`)} />
                    </div>
                </div>
            );
        }

        if (currentPaymentMethod) {
            return (
                <div className="u_px-20 u_py-16 u_bb-light">
                    {renderPaymentMethodDetails(currentPaymentMethod)}
                </div>
            );
        }

        return (
            <div className="u_px-20 u_py-16 u_bb-light">
                <p className="u_small-font u_color-font">No payment method linked to this subscription.</p>
            </div>
        );
    };

    return (
        <React.Fragment>
            <div className="u_layout-list">
                <AppHeader title="Payment Method" />
                <div className="u_layout-scrollview u_flex u_direction-column u_overflow-hidden">

                    {/* Critical: Card Expired Banner */}
                    {isCardExpired && (
                        <div className="u_flex-align u_content-between u_py-10 u_px-20 u_bg-red-1 u_bb-light">
                            <div className="u_flex-align">
                                <Icon.Warning size={18} fill={COLOR?.u_red} className="u_mr-8" />
                                <p className="u_small-font u_color-title u_fw-500">
                                    Your card has expired! Update your payment method to continue your subscription.
                                </p>
                            </div>
                            {canUpdate && (
                                <UButton
                                    type={updateLoading ? 'Processing...' : 'Update Now'}
                                    onClick={handleChangePaymentMethod}
                                    disabled={updateLoading}
                                    cn="u_ml-16 u_payment-btn"
                                />
                            )}
                        </div>
                    )}

                    {/* Warning: Card Expiring Soon Banner */}
                    {!isCardExpired && hasExpiryWarning && (
                        <div className="u_flex-align u_content-between u_py-10 u_px-20 u_bg-blue-1 u_bb-light">
                            <div className="u_flex-align">
                                <Icon.InfoTooltip size={18} fill={COLOR?.u_blue} className="u_mr-8" />
                                <p className="u_small-font u_color-title u_fw-500">
                                    Your card is expiring soon. Update your payment method to avoid service interruption.
                                </p>
                            </div>
                            {canUpdate && (
                                <UButton
                                    type={updateLoading ? 'Processing...' : 'Update Payment Method'}
                                    onClick={handleChangePaymentMethod}
                                    disabled={updateLoading}
                                    cn="u_ml-16 u_payment-btn"
                                />
                            )}
                        </div>
                    )}

                    {/* 409: Pending Modification Error — must resolve before updating payment method */}
                    {pendingModError && (
                        <div className="u_px-20 u_py-14 u_bg-blue-1 u_bb-light">
                            <div className="u_flex-align u_mb-10">
                                <Icon.InfoTooltip size={18} fill={COLOR?.u_blue} className="u_mr-8" />
                                <p className="u_font u_fw-600 u_color-title">Pending Plan Modification</p>
                            </div>
                            <p className="u_small-font u_color-font u_mb-10">
                                {pendingModError?.error || 'You have a pending plan modification. Please resolve it before updating your payment method.'}
                            </p>
                            {pendingModError?.pendingModification && (
                                <div className="u_flex-align u_mb-12">
                                    <p className="u_small-font u_color-font u_mr-20">
                                        <span className="u_fw-600">Status: </span>
                                        {pendingModError.pendingModification.status}
                                    </p>
                                    {pendingModError?.proratedOrder?.amount && (
                                        <p className="u_small-font u_color-font">
                                            <span className="u_fw-600">Amount Due: </span>
                                            {numberWithComaToFixed(pendingModError.proratedOrder.amount, subscription?.currency || 'INR')}
                                        </p>
                                    )}
                                </div>
                            )}
                            <div className="u_flex-align">
                                {pendingModError?.pendingModification?.status === 'AWAITING_PAYMENT' && pendingModError?.proratedOrder?.paymentLink && (
                                    <UButton
                                        type={`Pay ${numberWithComaToFixed(pendingModError.proratedOrder.amount, subscription?.currency || 'INR')} Now`}
                                        variant="blue"
                                        onClick={handleCompleteModPayment}
                                        cn="u_mr-10 u_payment-btn"
                                    />
                                )}
                                <UButton
                                    type={cancelModLoading ? 'Cancelling...' : 'Cancel Modification'}
                                    variant="white"
                                    onClick={handleCancelModification}
                                    disabled={cancelModLoading}
                                    cn="u_payment-btn"
                                />
                            </div>
                        </div>
                    )}

                    <div className="u_flex-1 u_overflow-hidden">
                        <UCustomScrollBar>
                            <div className="u_px-20 u_pt-20 u_pb-20">

                                {/* ── Section: Payment Method ── */}
                                <div className="u_mb-20">
                                    <div className="u_b-blue u_radius-8 u_overflow-hidden">
                                        <div className="u_bg-lightblue u_px-20 u_py-16 u_flex-align u_content-between">
                                            <div className="u_flex-align">
                                                <div className="u_bg-white u_radius-8 u_p-12 u_b-light u_mr-16 u_flex-shrink">
                                                    <Icon.CreditCard size={24} fill={COLOR?.u_blue} />
                                                </div>
                                                <div>
                                                    <p className="u_small-font u_color-font u_mb-4">
                                                        {currentPaymentMethod
                                                            ? getPaymentMethodTypeLabel(currentPaymentMethod.type)
                                                            : 'Payment Method'}
                                                    </p>
                                                    <p className="u_font-16 u_fw-700 u_color-title">
                                                        {currentPaymentMethod?.displayLabel || (dataLoading ? '-' : 'Not configured')}
                                                    </p>
                                                </div>
                                            </div>
                                            {currentPaymentMethod && (
                                                <UTags
                                                    className="u_ib u_flex-shrink"
                                                    title={currentPaymentMethod.authorizationStatus === 'ACTIVE' ? 'Active' : currentPaymentMethod.authorizationStatus === 'REVOKED' ? 'Revoked' : currentPaymentMethod.authorizationStatus || 'Unknown'}
                                                    color={currentPaymentMethod.authorizationStatus === 'ACTIVE' ? '#60B158' : '#FF6961'}
                                                />
                                            )}
                                        </div>

                                        {renderPaymentMethodZone()}

                                        {canUpdate && paymentMethodStatus === 'AVAILABLE' && (
                                            <div className="u_bg-thead u_px-20 u_py-12 u_flex-align u_content-between">
                                                <div className="u_flex-align">
                                                    <Icon.Lock size={16} fill={COLOR?.u_font} className="u_mr-6" />
                                                    <p className="u_small-font u_color-font">Secured by Cashfree Payments</p>
                                                </div>
                                                <div>
                                                    {/* <UButton
                                                        type={updateLoading ? 'Processing...' : 'Change Payment Method'}
                                                        variant="blue"
                                                        onClick={handleChangePaymentMethod}
                                                        disabled={updateLoading}
                                                        cn="u_payment-btn"
                                                        style={{ padding: '8px', borderRadius: '6px' }}
                                                    /> */}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {subscription && (
                                    <div className="u_mb-20 u_b-light u_radius-8 u_p-20">
                                        <p className="u_font-16 u_fw-700 u_color-title u_mb-16">Current Subscription</p>
                                        <div className="u_row">
                                            <div className="u_col-3">
                                                <div className="u_bg-thead u_radius-8 u_px-14 u_py-12 u_b-light">
                                                    <p className="u_small-font u_color-font u_mb-6">Plan</p>
                                                    <p className="u_small-font u_fw-600 u_color-title">{subscription?.planCode ?? subscription?.planKey ?? '-'}</p>
                                                </div>
                                            </div>
                                            <div className="u_col-3">
                                                <div className="u_bg-thead u_radius-8 u_px-14 u_py-12 u_b-light">
                                                    <p className="u_small-font u_color-font u_mb-6">Billing Cycle</p>
                                                    <p className="u_small-font u_fw-600 u_color-title">{subscription?.billingCycle || '-'}</p>
                                                </div>
                                            </div>
                                            <div className="u_col-3">
                                                <div className="u_bg-thead u_radius-8 u_px-14 u_py-12 u_b-light">
                                                    <p className="u_small-font u_color-font u_mb-6">Seats</p>
                                                    <p className="u_small-font u_fw-600 u_color-title">{subscription?.seatCount ?? '-'}</p>
                                                </div>
                                            </div>
                                            <div className="u_col-3">
                                                <div className="u_bg-thead u_radius-8 u_px-14 u_py-12 u_b-light">
                                                    <p className="u_small-font u_color-font u_mb-6">Amount</p>
                                                    <p className="u_small-font u_fw-600 u_color-title">
                                                        {subscription?.totalAmount
                                                            ? numberWithComaToFixed(subscription.totalAmount, subscription.currency || 'INR')
                                                            : '-'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="u_col-3 u_mt-10">
                                                <div className="u_bg-thead u_radius-8 u_px-14 u_py-12 u_b-light">
                                                    <p className="u_small-font u_color-font u_mb-6">Status</p>
                                                    <p className="u_small-font u_fw-600 u_color-title">{subscription?.statusLabel || '-'}</p>
                                                </div>
                                            </div>
                                            {subscription?.nextBillingDate && (
                                                <div className="u_col-3 u_mt-10">
                                                    <div className="u_bg-thead u_radius-8 u_px-14 u_py-12 u_b-light">
                                                        <p className="u_small-font u_color-font u_mb-4">Next Billing Date</p>
                                                        <p className="u_small-font u_fw-600 u_color-title">
                                                            {dateFormatter({ date: subscription.nextBillingDate, format: 'DD MMM, YYYY' })}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {paymentMethodStatus !== 'NO_SUBSCRIPTION' && (
                                    <div>
                                        <div className="u_flex-align u_content-between u_mb-14">
                                            <p className="u_semi-large-font u_fw-700 u_color-title">Saved Cards</p>
                                            {savedInstruments?.length > 0 && (
                                                <span className="u_small-font u_color-font u_bg-thead u_radius-5 u_px-10 u_py-4">
                                                    {savedInstruments?.length} card{savedInstruments?.length !== 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </div>

                                        {savedInstruments?.length > 0 ? (
                                            <div className="u_row">
                                                {savedInstruments?.map((data, i) => (
                                                    <div key={i} className="u_col-3 u_mb-16">
                                                        <div className="u_b-light u_radius-8 u_overflow-hidden">
                                                            <div className="u_bg-thead u_px-20 u_pt-18 u_pb-16">
                                                                <div className="u_flex-align u_content-between u_mb-20">
                                                                    <div className="u_flex-align">
                                                                        <Icon.CreditCard size={18} fill={COLOR?.u_icon_bg} />
                                                                        <p className="u_small-font u_fw-700 u_color-title u_ml-6">{data.bankName || 'Bank Card'}</p>
                                                                    </div>
                                                                    <UPopup
                                                                        params={{ className: 'u_tooltip', position: 'bottom right' }}
                                                                        desc={<span>Delete Card</span>}
                                                                    >
                                                                        <span className="u_cp" onClick={() => !deleteLoadingId && handleDeleteCard(data)}>
                                                                            {deleteLoadingId === data.instrumentId
                                                                                ? <Icon.Preloader size={16} />
                                                                                : <Icon.Delete size={16} fill={COLOR?.u_blue} />
                                                                            }
                                                                        </span>
                                                                    </UPopup>
                                                                </div>

                                                                <p className="u_font-16 u_fw-700 u_color-title u_mb-18">{data.displayLabel}</p>

                                                                <div className="u_flex-align u_content-between">
                                                                    <div>
                                                                        <p className="u_font u_color-font u_mb-4">VALID THRU</p>
                                                                        <p className="u_small-font u_fw-700 u_color-title">
                                                                            {data.expiryMonth && data.expiryYear
                                                                                ? `${data.expiryMonth}/${data.expiryYear}`
                                                                                : '— / ——'
                                                                            }
                                                                        </p>
                                                                    </div>
                                                                    <div className="u_flex-align">
                                                                        <span className="u_small-font u_fw-700 u_color-title">
                                                                            {getKeyName(PAYMENT_CARD_TYPE, data?.cardType)?.label || data?.cardType}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="u_b-dash-light u_radius-8 u_p-20 u_text-center">
                                                {dataLoading ? (
                                                    <div className="u_row">
                                                        {[...Array(4)].map((_, i) => (
                                                            <div key={i} className="u_col-3 u_mb-16">
                                                                <Skeleton height={140} className="u_radius-8" />
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="u_mb-14 u_ib">
                                                            <UIcon color={COLOR?.u_icon_bg} thumb={50} icon={<Icon.CreditCard size={28} fill={COLOR?.u_icon_bg} />} type="square" />
                                                        </div>
                                                        <p className="u_font-16 u_fw-600 u_color-title u_mb-6">No Saved Cards</p>
                                                        <p className="u_small-font u_color-font">Cards saved during checkout will appear here.</p>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                            </div>
                        </UCustomScrollBar>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default PaymentMethod;
